import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@wot/components';
import { useClickOutside } from '@wot/shared';
import { useEffect, useRef, useState } from 'react';
import tutorsApi from '../../../api/tutors';
import { SPINNER } from '../../../assets/animated';
import { FILTER_SVG } from '../../../assets/icons';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import { TabBar } from '../../../components/TabBar';
import { TabItem } from '../../../components/TabItem';
import usePagainate from '../../../hooks/usePaginate';
import { useTab } from '../../../hooks/useTab';
import TableContainer from './components/Table';

interface IFilters {
  categoryType: string,
  level: string
}

export type ISorting = "createdAt"

export default function ManageTutors() {
  const queryClient = useQueryClient();
  const { activeTab, onTabChange } = useTab('all');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<IFilters | {}>({});

  useEffect(() => {
    resetPage();
  }, [activeTab]);

  const handleSort = (key: ISorting) => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['manage-tutors', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () =>
      await tutorsApi.get(
        currentPage,
        resultsPerPage,
        activeTab === 'all'
          ? { ...filters }
          : { status: activeTab, ...filters },
        { ...sortBy },
      )
  });

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['manage-tutors', currentPage + 1],
        queryFn: async () =>
          await tutorsApi.get(
            currentPage,
            resultsPerPage,
            activeTab === 'all'
              ? { ...filters }
              : { status: activeTab, ...filters },
            { ...sortBy },
          )
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  const filterResults = (filters: any) => {
    setFilters(filters);
  }

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Tutors List</h1>
      <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="text-primary">
        <TabItem label="All" value="all" />
        <TabItem label="Under Review" value="under-review" />
        <TabItem label="Revision" value="revision" />
        <TabItem label="Tutor Descision" value="rev-agreement" />
        <TabItem label="Active" value="active" />
        <TabItem label="In Active" value="in-active" />
        <TabItem label="Blocked" value="blocked" />
      </TabBar>
      <Filters onApply={filterResults} onClear={() => setFilters({})} filters={filters} />
      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data.results.length > 0 ? (
        <>
          <TableContainer handleSort={handleSort} data={data.results} />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
    </div>
  );
}



interface Props {
  onApply: (filters: any) => void
  onClear: () => void,
  filters: IFilters | {}
}
function Filters({ filters, onApply, onClear }: Props) {
  const [showOptions, setShowOptions] = useState(false);
  const filterSortRef = useRef(null);

  const [temp, setTemp] = useState<IFilters>({
    categoryType: "",
    level: ""
  });

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      // @ts-ignore
      setTemp(filters);
    }
  }, [filters]);

  useClickOutside(filterSortRef, () => setShowOptions(false));

  return (
    <div className='relative flex justify-end'>
      <div onClick={() => setShowOptions(true)} className={`w-7 h-7 ${Object.keys(filters).length > 0 ? "bg-blue-100" : "bg-white"} flex items-center justify-center cursor-pointer border border-gray-300 rounded-md p-1 drop-shadow-sm`}>
        <img src={FILTER_SVG} />
      </div>
      {showOptions &&
        <div
          ref={filterSortRef}
          className="md:min-w-[500px] min-w-full flex flex-col border border-neutralBlue1 absolute top-14 md:right-5 right-0 p-6 bg-white drop-shadow-lg rounded-lg z-40"
        >
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Category</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, categoryType: "" })}
              >
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 sm:gap-0 justify-between">
              <div
                className={`${temp.categoryType === 'subject' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "subject" })}
              >
                Subject
              </div>
              <div
                className={`${temp.categoryType === 'hobby' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "hobby" })}
              >
                Hobby
              </div>
              <div
                className={`${temp.categoryType === 'skill' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, categoryType: "skill" })}
              >
                Skill Development
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Level</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, level: "" })}>
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 justify-between">
              <div
                className={`${temp.level === 'standard' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, level: "standard" })}>
                Standard
              </div>
              <div
                className={`${temp.level === 'premium' ? 'bg-neutralBlue2' : 'bg-white'
                  } w-full  border border-neutralBlue1 text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, level: "premium" })}>
                Premium
              </div>
            </div>
          </div>

          <div className="flex mt-5 gap-7">
            <Button
              text="Clear All"
              block
              type="s-outlined"
              onClick={() => {
                onClear();
                setShowOptions(false)
              }}
            />
            <Button
              text="Apply"
              block
              type="primary"
              onClick={() => {
                onApply(temp)
                setShowOptions(false);
              }}
            />
          </div>
        </div>
      }
    </div >
  )
}

